import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BlobField from '../components/BlobField';
import Toast from '../components/Toast';
import FeatureClanSheet from '../components/FeatureClanSheet';
import CoverUpload from '../components/CoverUpload';
import ShareClanModal from '../components/ShareClanModal';
import api, { formatApiErrorDetail } from '../api';
import { useAuth } from '../context/AuthContext';
import { isSqrtClan, fmtBits } from '../lib/clan';
import ReplayModal from '../components/ReplayModal';

/**
 * Phase 2 · Step 2 — Manage Clan Dashboard.
 * Reference: /app/reference/09-manage-clan.html
 *
 * Editable: min_bits_to_join, perks, custom_perk, is_paused.
 * Read-only: multiplier (locked for holder protection), bits_minted.
 * Stubbed: New Post / Go Live (ship in Phase 2 · Step 3 — Clan Feed).
 * Stubbed: Get Verified / Featured / Schedule Launch (Phase 4).
 */

const PERK_OPTIONS = [
  { key: 'live-qa', label: '🎙️ Weekly live Q&A', color: 'orange' },
  { key: 'breakdowns', label: '📊 Exclusive breakdowns', color: 'pink' },
  { key: 'priority-dm', label: '💬 Priority DM replies', color: 'blue' },
  { key: 'early-access', label: '⏱️ Early access to posts', color: 'green' },
  { key: 'voice-chat', label: '🎧 Members-only voice chats', color: 'purple' },
  { key: 'raffle', label: '🎁 Monthly holder raffle', color: 'yellow' },
];

const PRESET_LABELS = new Set(PERK_OPTIONS.map((p) => p.label));

const formatCompact = (n) => {
  if (n >= 10000) return `${(n / 1000).toFixed(1)}K`;
  return n.toLocaleString();
};

/**
 * Format a pts amount with adaptive precision:
 *   - < 1  → 4 decimals  (e.g. "0.0909 pts")
 *   - >= 1 → 2 decimals  (e.g. "500.00 pts")
 * Returns "—" for missing/invalid values.
 */
const formatPts = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return '—';
  const digits = num >= 1 ? 2 : 4;
  return `${num.toFixed(digits)} pts`;
};

export default function ManageClan() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useTranslation();

  const goBack = (fallback = '/home') => {
    // Prefer real history when the user arrived here from within the app
    // (e.g. the gear icon on Clan Feed). location.key === 'default' means
    // this was the initial entry — in that case fall back to a safe route.
    if (location.key && location.key !== 'default') navigate(-1);
    else navigate(fallback);
  };

  const [clan, setClan] = useState(undefined); // undefined=loading, null=no clan, obj=clan
  const [stats, setStats] = useState(null);
  const [minBits, setMinBits] = useState(1);
  const [selectedPerks, setSelectedPerks] = useState([]);
  const [customPerk, setCustomPerk] = useState('');
  const [paused, setPaused] = useState(false);
  const [coverUrl, setCoverUrl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [error, setError] = useState('');
  const [featureOpen, setFeatureOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [replayModalOpen, setReplayModalOpen] = useState(false);
  const [demoStatus, setDemoStatus] = useState(null);
  const [demoBusy, setDemoBusy] = useState(false);
  // Which tab of the Manage Clan console is active. Defaults to
  // `settings` (day-to-day tweaks). `?tab=grow` in the URL jumps
  // creators straight to Get Verified / Get Featured — used by the
  // owner-strip on Public Profile so those two CTAs are 1 tap away.
  const initialTab = new URLSearchParams(location.search).get('tab') || 'settings';
  const [activeTab, setActiveTab] = useState(
    ['settings', 'grow', 'danger'].includes(initialTab) ? initialTab : 'settings'
  );

  const hydrate = (c) => {
    setMinBits(c.min_bits_to_join);
    const presetFromServer = (c.perks || []).filter((p) => PRESET_LABELS.has(p));
    setSelectedPerks(presetFromServer);
    setCustomPerk(c.custom_perk || '');
    setPaused(!!c.is_paused);
    setCoverUrl(c.cover_url || null);
  };

  useEffect(() => {
    Promise.all([
      api.get('/clans/mine').catch(() => ({ data: { clan: null } })),
      api.get('/clans/mine/stats').catch(() => ({ data: null })),
    ]).then(([mine, s]) => {
      setClan(mine.data.clan);
      setStats(s.data);
      if (mine.data.clan) hydrate(mine.data.clan);
    });
  }, []);

  // Poll demo status + stats when the demo creator is viewing this page so
  // the "Start Launch Simulation" banner and the Bits/Holders/Royalties stat
  // strip refresh in real-time during a running simulation.
  useEffect(() => {
    if (!user || user.demo_pod !== 'launch_sim' || user.demo_role !== 'creator') return;
    let stopped = false;
    const tick = async () => {
      try {
        const { data } = await api.get('/demo/status');
        if (!stopped) setDemoStatus(data);
        if (data?.running) {
          // Live refresh: pull latest clan + stats so the UI keeps up with buys
          const [mine, s] = await Promise.all([
            api.get('/clans/mine').catch(() => ({ data: { clan: null } })),
            api.get('/clans/mine/stats').catch(() => ({ data: null })),
          ]);
          if (!stopped) {
            if (mine.data.clan) setClan(mine.data.clan);
            if (s.data) setStats(s.data);
          }
        }
      } catch { /* ignore */ }
    };
    tick();
    const iv = setInterval(tick, 2500);
    return () => { stopped = true; clearInterval(iv); };
  }, [user]);

  const startDemoLaunch = async () => {
    if (demoBusy) return;
    setDemoBusy(true);
    try {
      const { data } = await api.post('/demo/launch');
      setToast({ msg: data?.message || 'Launch triggered ✓' });
    } catch (ex) {
      setToast({ msg: formatApiErrorDetail(ex.response?.data?.detail) || 'Could not start launch.', kind: 'error' });
    } finally {
      setDemoBusy(false);
    }
  };

  const isDirty = useMemo(() => {
    if (!clan) return false;
    const originalPresets = (clan.perks || []).filter((p) => PRESET_LABELS.has(p));
    const same =
      minBits === clan.min_bits_to_join &&
      paused === !!clan.is_paused &&
      customPerk === (clan.custom_perk || '') &&
      (coverUrl || null) === (clan.cover_url || null) &&
      selectedPerks.length === originalPresets.length &&
      selectedPerks.every((p) => originalPresets.includes(p));
    return !same;
  }, [clan, minBits, paused, customPerk, selectedPerks, coverUrl]);

  const togglePerk = (label) => {
    setSelectedPerks((prev) => (
      prev.includes(label) ? prev.filter((x) => x !== label) : [...prev, label]
    ));
  };

  const discardChanges = () => {
    if (!clan) return;
    hydrate(clan);
    setError('');
  };

  const save = async () => {
    if (!isDirty || saving) return;
    setSaving(true);
    setError('');
    try {
      const payload = {
        min_bits_to_join: minBits,
        perks: selectedPerks,
        custom_perk: customPerk.slice(0, 80),
        is_paused: paused,
        cover_url: coverUrl || '',
      };
      const { data } = await api.patch('/clans/mine', payload);
      setClan(data.clan);
      hydrate(data.clan);
      // Refresh stats after min_bits change (may affect holders count)
      api.get('/clans/mine/stats').then(({ data: s }) => setStats(s)).catch(() => {});
      setToast({ msg: '✓ Saved' });
    } catch (ex) {
      setError(formatApiErrorDetail(ex.response?.data?.detail) || 'Could not save changes.');
    } finally {
      setSaving(false);
    }
  };

  const showComingSoon = (label) => setToast({ msg: `🚧 ${label} — coming soon.` });

  // ---- Guards ----

  if (clan === undefined) {
    return (
      <>
        <BlobField />
        <div className="loading-shell" data-testid="mc-loading">{t('manageClan.loading')}</div>
      </>
    );
  }

  if (clan === null) {
    return (
      <>
        <BlobField />
        <div className="mc-wrap" data-testid="mc-no-clan">
          <div className="mc-topbar">
            <button className="mc-icon-btn" onClick={() => goBack('/home')} aria-label={t('manageClan.backAria')}>
              <svg viewBox="0 0 24 24" fill="none" width="17" height="17">
                <path d="M15 4l-8 8 8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
          <div className="mc-section-card" style={{ textAlign: 'center' }}>
            <h2 style={{ fontFamily: "'Baloo 2', sans-serif", fontSize: 22, marginBottom: 10 }}>
              {t('manageClan.noClanTitle')}
            </h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: 20 }}>
              {t('manageClan.noClanBody')}
            </p>
            <button className="mc-btn-primary" onClick={() => navigate('/create-clan')} data-testid="mc-goto-create">
              {t('manageClan.startMyClan')}
            </button>
          </div>
        </div>
      </>
    );
  }

  const initials = (user?.display_name || clan.handle || 'U')
    .split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase();
  const gradient = user?.gradient
    ? `linear-gradient(135deg, ${user.gradient[0]}, ${user.gradient[1]})`
    : 'linear-gradient(135deg, #FF5722, #FFC107)';

  const isDemoCreator = user?.demo_pod === 'launch_sim' && user?.demo_role === 'creator';

  return (
    <>
      <BlobField />
      <div className="mc-wrap" data-testid="manage-clan">
        {/* Top bar */}
        <div className="mc-topbar">
          <button className="mc-icon-btn" onClick={() => goBack(clan?.handle ? `/clan/${clan.handle}/feed` : '/home')} aria-label={t('manageClan.backAria')} data-testid="mc-back-btn">
            <svg viewBox="0 0 24 24" fill="none" width="17" height="17">
              <path d="M15 4l-8 8 8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <span className="mc-top-title">{t('manageClan.topTitle')}</span>
          <div className="mc-top-actions">
            <button
              type="button"
              className="mc-share-btn"
              onClick={() => setShareOpen(true)}
              data-testid="mc-share-clan-btn"
              title={t('manageClan.shareMyClanTitle')}
            >
              <span aria-hidden="true">📣</span> {t('manageClan.shareMyClan')}
            </button>
            <button
              type="button"
              className="mc-view-chart-btn"
              onClick={() => navigate(`/trade?clan=${clan.handle}`)}
              data-testid="mc-view-live-chart"
              title="Watch your Clan trading live"
            >
              <span aria-hidden="true">📈</span> View live chart
            </button>
            <button
              className="mc-view-public"
              onClick={() => navigate(`/creator/${clan.handle}`)}
              data-testid="mc-view-public"
            >
              {t('manageClan.viewPublicPage')}
            </button>
            <button
              type="button"
              className="mc-view-public"
              data-testid="mc-download-replay"
              title={t('manageClan.signedReplayTitle')}
              onClick={() => setReplayModalOpen(true)}
            >
              {t('manageClan.signedReplay')}
            </button>
          </div>
        </div>

        {/* Summary card */}
        <div className="mc-summary" data-testid="mc-summary">
          <div className="mc-summary-row">
            <div className="mc-summary-avatar" style={{ background: gradient }}>{initials}</div>
            <div>
              <div className="mc-summary-name">{user?.display_name || clan.handle}{t('manageClan.summaryClanSuffix')}</div>
              <div className="mc-summary-handle">@{clan.handle}</div>
            </div>
          </div>
          <div className="mc-stat-strip">
            <div className="mc-stat-mini" data-testid="mc-stat-holders">
              <div className="mc-stat-val">{formatCompact(stats?.holders || 0)}</div>
              <div className="mc-stat-lbl">{t('manageClan.statHolders')}</div>
            </div>
            <div className="mc-stat-mini" data-testid="mc-stat-bits-minted">
              <div className="mc-stat-val">{formatCompact(stats?.bits_minted ?? clan.bits_minted)}</div>
              <div className="mc-stat-lbl">{t('manageClan.statBitsMinted')}</div>
            </div>
            <div className="mc-stat-mini" data-testid="mc-stat-total-earned">
              <div className="mc-stat-val">{(stats?.total_earnings || stats?.royalties_earned || 0).toFixed(2)} {t('common.pts')}</div>
              <div className="mc-stat-lbl">{t('manageClan.statTotalEarned')}</div>
            </div>
          </div>

          {/* Earnings breakdown — Trading vs Perks (Task 6) */}
          {stats && (
            <div
              style={{
                marginTop: 10, padding: '10px 12px', borderRadius: 10,
                background: 'rgba(0,180,255,0.04)',
                border: '1px solid rgba(0,180,255,0.15)',
                display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'space-around', fontSize: 12,
              }}
              data-testid="mc-earnings-breakdown"
            >
              <div style={{ textAlign: 'center' }}>
                <div style={{ opacity: 0.65, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10 }}>
                  {t('manageClan.tradingRoyalties')}
                </div>
                <div style={{ fontWeight: 700, fontSize: 15, marginTop: 2 }}>
                  {(stats.royalties_earned || 0).toFixed(2)} {t('common.pts')}
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ opacity: 0.65, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10 }}>
                  {t('manageClan.perksRevenue')}
                </div>
                <div style={{ fontWeight: 700, fontSize: 15, marginTop: 2, color: (stats.perks_revenue || 0) > 0 ? '#4ade80' : 'inherit' }}>
                  {(stats.perks_revenue || 0).toFixed(2)} {t('common.pts')}
                </div>
              </div>
              {stats.perks_pending_count > 0 && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ opacity: 0.65, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10 }}>
                    {t('manageClan.pendingDeliveries')}
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 15, marginTop: 2, color: '#FFB74D' }}>
                    {stats.perks_pending_count}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Manage Clan tab bar — puts the two highest-value growth CTAs
            (Verified + Featured) in their own tab so creators don't scroll
            past all the settings to find them. `?tab=grow` in the URL jumps
            straight here (linked from the Public-Profile owner strip). */}
        <div className="mc-tabs" role="tablist" data-testid="mc-tabs">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'settings'}
            className={`mc-tab${activeTab === 'settings' ? ' active' : ''}`}
            onClick={() => setActiveTab('settings')}
            data-testid="mc-tab-settings"
          >
            <span aria-hidden="true">⚙</span> Settings
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'grow'}
            className={`mc-tab${activeTab === 'grow' ? ' active' : ''}`}
            onClick={() => setActiveTab('grow')}
            data-testid="mc-tab-grow"
          >
            <span aria-hidden="true">⭐</span> Grow
            {!user?.verified && <span className="mc-tab-dot" aria-label="Action available" />}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'danger'}
            className={`mc-tab${activeTab === 'danger' ? ' active' : ''}`}
            onClick={() => setActiveTab('danger')}
            data-testid="mc-tab-danger"
          >
            <span aria-hidden="true">⚠</span> Danger Zone
          </button>
        </div>


        {/* Demo Launch Simulation banner — only for the demo creator */}
        {isDemoCreator && (
          <div
            style={{
              margin: '16px 0',
              padding: 20,
              borderRadius: 14,
              background: demoStatus?.running
                ? 'linear-gradient(135deg, rgba(74,222,128,0.14), rgba(0,180,255,0.10))'
                : 'linear-gradient(135deg, rgba(0,180,255,0.14), rgba(181,132,245,0.14))',
              border: `1px solid ${demoStatus?.running ? 'rgba(74,222,128,0.35)' : 'rgba(0,180,255,0.35)'}`,
            }}
            data-testid="demo-launch-banner"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 260px' }}>
                <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 1.3, opacity: 0.75, marginBottom: 6 }}>
                  {t('manageClan.demoEyebrow')}
                </div>
                <div style={{ fontSize: 20, fontWeight: 800, lineHeight: 1.2 }}>
                  {demoStatus?.running
                    ? `${demoStatus.phase?.[0].toUpperCase()}${demoStatus.phase?.slice(1)} ${t('manageClan.demoPhaseSuffix')}`
                    : demoStatus?.phase === 'complete'
                    ? t('manageClan.demoComplete')
                    : t('manageClan.demoReady')}
                </div>
                <div style={{ fontSize: 13, opacity: 0.85, marginTop: 6 }}>
                  {demoStatus?.running
                    ? t('manageClan.demoRunningBody')
                    : t('manageClan.demoIdleBody')}
                </div>
              </div>
              <button
                type="button"
                className="mc-btn-primary"
                onClick={startDemoLaunch}
                disabled={demoBusy || demoStatus?.running}
                data-testid="demo-launch-btn"
                style={{ minWidth: 200 }}
              >
                {demoStatus?.running
                  ? `${t('manageClan.demoLive')} · ${demoStatus.phase || 'starting'}`
                  : demoBusy
                  ? t('manageClan.demoStarting')
                  : t('manageClan.demoStartCta')}
              </button>
            </div>
          </div>
        )}

        {/* Clan info — merged parameters + pricing model into a single
            read-only info card (2026-02-17 reorg). */}
        {activeTab === 'settings' && (<>
        <div className="mc-section-card" data-testid="mc-clan-info">
          <div className="mc-section-head">
            <span className="mc-section-title">{t('manageClan.clanInfoTitle')}</span>
            <span className="mc-lock-badge">
              <svg viewBox="0 0 20 20" fill="none" width="10" height="10">
                <rect x="4" y="9" width="12" height="8" rx="2" stroke="currentColor" strokeWidth="1.5" />
                <path d="M6.5 9V6a3.5 3.5 0 017 0v3" stroke="currentColor" strokeWidth="1.5" />
              </svg>
              {t('manageClan.immutable')}
            </span>
          </div>
          <p className="mc-section-sub">
            {t('manageClan.clanInfoLede')}
          </p>

          <div className="mc-info-subhead">{t('manageClan.parameters')}</div>
          <div className="mc-params-grid" data-testid="mc-clan-params">
            <div className="mc-param" data-testid="mc-param-audience">
              <div className="mc-param-lbl">{t('manageClan.audience')}</div>
              <div className="mc-param-val">{clan.audience_bucket || '—'}</div>
              <div className="mc-param-tag">{t('manageClan.chosen')}</div>
            </div>
            <div className="mc-param" data-testid="mc-param-ltv">
              <div className="mc-param-lbl">{t('manageClan.lifetimeValue')}</div>
              <div className="mc-param-val">${clan.lifetime_value ? Math.round(clan.lifetime_value).toLocaleString() : '—'}</div>
              <div className="mc-param-tag">{t('manageClan.chosen')}</div>
            </div>
            <div className="mc-param" data-testid="mc-param-vibe">
              <div className="mc-param-lbl">{t('manageClan.vibe')}</div>
              <div className="mc-param-val" style={{ textTransform: 'capitalize' }}>{clan.vibe || '—'}</div>
              <div className="mc-param-tag">{t('manageClan.chosen')}</div>
            </div>
            <div className="mc-param" data-testid="mc-param-supply">
              <div className="mc-param-lbl">{t('manageClan.supplyCap')}</div>
              <div className="mc-param-val">
                {isSqrtClan(clan) ? (
                  <span title={t('manageClan.supplyCapUnlimitedTitle')}>{t('manageClan.supplyCapUnlimited')}</span>
                ) : formatCompact(clan.supply_cap || 0)}
              </div>
              <div className="mc-param-tag derived">
                {isSqrtClan(clan) ? t('manageClan.sqrtCurve') : t('manageClan.derived')}
              </div>
            </div>
            <div className="mc-param" data-testid="mc-param-max">
              <div className="mc-param-lbl">{t('manageClan.maxPerMember')}</div>
              <div className="mc-param-val">
                {isSqrtClan(clan)
                  ? <span title={t('manageClan.maxPerMemberDynamicTitle')}>{t('manageClan.maxPerMemberDynamic')}</span>
                  : formatCompact(clan.max_bits_per_user || 0)}
              </div>
              <div className="mc-param-tag derived">
                {isSqrtClan(clan) ? t('manageClan.antiWhale') : t('manageClan.derived')}
              </div>
            </div>
            <div className="mc-param" data-testid="mc-param-min">
              <div className="mc-param-lbl">{t('manageClan.minToJoin')}</div>
              <div className="mc-param-val">{clan.min_bits_to_join || '—'}</div>
              <div className="mc-param-tag derived">{t('manageClan.editable')}</div>
            </div>
          </div>

          <div className="mc-info-subhead">{t('manageClan.pricing')}</div>
          <div className="mc-pricing-grid">
            <div className="mc-pricing-tile">
              <div className="mc-pricing-label">{t('manageClan.startingBitPrice')}</div>
              <div className="mc-pricing-value" data-testid="mc-start-price">
                {formatPts(clan.multiplier)}
              </div>
              <div className="mc-pricing-sub">{t('manageClan.startingBitPriceSub')}</div>
            </div>
            <div className="mc-pricing-tile">
              <div className="mc-pricing-label">{t('manageClan.finalBitPrice')}</div>
              <div className="mc-pricing-value" data-testid="mc-top-price">
                {formatPts(clan.lifetime_value)}
              </div>
              <div className="mc-pricing-sub">{t('manageClan.finalBitPriceSub')}</div>
            </div>
            <div className="mc-pricing-tile">
              <div className="mc-pricing-label">{t('manageClan.currentBitPrice')}</div>
              <div className="mc-pricing-value mc-pricing-value-live" data-testid="mc-current-price">
                {formatPts(clan.current_price)}
              </div>
              <div className="mc-pricing-sub">{t('manageClan.currentBitPriceSub')}</div>
            </div>
            <div className="mc-pricing-tile">
              <div className="mc-pricing-label">
                {t('manageClan.priceGrowthPerBit')}
                <span className="mc-pricing-tech">{t('manageClan.priceGrowthMultiplier', { mult: clan.multiplier.toFixed(2) })}</span>
              </div>
              <div className="mc-pricing-value" data-testid="mc-multiplier">
                +{formatPts(clan.multiplier)}
              </div>
              <div className="mc-pricing-sub">{t('manageClan.priceGrowthPerBitSub')}</div>
            </div>
          </div>
        </div>

        {/* Cover picture — optional banner shown across Creator Profile,
            Clan Feed, and Manage Clan. Falls back to the auto-gradient if
            no cover is set. Empty string in the PATCH payload clears it. */}
        <div className="mc-section-card" data-testid="mc-cover-section">
          <div className="mc-section-head">
            <span className="mc-section-title">{t('manageClan.coverPicture')}</span>
          </div>
          <p className="mc-section-sub">
            {t('manageClan.coverPictureLede')}
          </p>
          <CoverUpload
            value={coverUrl}
            onChange={setCoverUrl}
            onError={(msg) => setToast({ msg })}
            disabled={saving}
            testIdPrefix="mc-cover"
          />
        </div>

        {/* Perks · Bounties · Members are now managed inline from the Clan
            Feed tabs (2026-02-17 reorg). Manage Clan stays focused on
            identity + configuration.  Tap the Feed / Perks / Bounties /
            Members tabs on your Clan page to run those areas directly. */}

        {/* Entry threshold */}
        <div className="mc-section-card">
          <div className="mc-section-head">
            <span className="mc-section-title">{t('manageClan.entryThreshold')}</span>
          </div>
          <p className="mc-section-sub">{t('manageClan.entryThresholdLede')}</p>
          <div className="mc-qty-stepper">
            <button
              className="mc-qty-btn"
              onClick={() => setMinBits((v) => Math.max(1, v - 1))}
              disabled={minBits <= 1}
              data-testid="mc-min-minus"
            >−</button>
            <input
              type="number"
              inputMode="numeric"
              min={1}
              max={10000}
              step={1}
              value={minBits}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '') { setMinBits(1); return; }
                const n = parseInt(raw, 10);
                if (Number.isNaN(n)) return;
                setMinBits(Math.max(1, Math.min(10000, n)));
              }}
              onBlur={(e) => {
                // Snap to a valid integer on blur (safety net)
                const n = parseInt(e.target.value || '1', 10);
                setMinBits(Number.isNaN(n) ? 1 : Math.max(1, Math.min(10000, n)));
              }}
              className="mc-qty-input"
              aria-label="Bits required to join"
              data-testid="mc-min-input"
            />
            <button
              className="mc-qty-btn"
              onClick={() => setMinBits((v) => Math.min(10000, v + 1))}
              disabled={minBits >= 10000}
              data-testid="mc-min-plus"
            >+</button>
          </div>
          <div className="mc-qty-caption">{t('manageClan.entryThresholdCaption')}</div>
          <div className="mc-warn-note" data-testid="mc-warn-existing">
            <svg viewBox="0 0 20 20" fill="none" width="15" height="15">
              <path d="M10 2L2 17h16L10 2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
              <path d="M10 8v4M10 14.5v.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <span>
              {t('manageClan.entryThresholdWarn', { count: stats?.holders || 0 })}
            </span>
          </div>
        </div>

        {/* Membership benefits (free chips) — free descriptive signals shown
            pre-purchase to sell the clan. NOT redeemable, just labels.
            Renamed from "Clan perks" to disambiguate from the redeemable
            Perks Marketplace section below. */}
        <div className="mc-section-card">
          <div className="mc-section-head">
            <span className="mc-section-title">{t('manageClan.membershipBenefits')}</span>
            <span className="mc-section-badge" style={{ background: 'rgba(180,255,0,0.12)', color: '#B4FF00', border: '1px solid rgba(180,255,0,0.35)' }}>
              {t('manageClan.membershipBenefitsBadge')}
            </span>
          </div>
          <p className="mc-section-sub">
            {t('manageClan.membershipBenefitsLede')}
          </p>
          <div className="mc-perk-grid" data-testid="mc-perk-grid">
            {PERK_OPTIONS.map((p) => (
              <div
                key={p.key}
                className={`mc-perk-chip mc-${p.color}${selectedPerks.includes(p.label) ? ' selected' : ''}`}
                onClick={() => togglePerk(p.label)}
                role="button"
                tabIndex={0}
                data-testid={`mc-perk-${p.key}`}
              >
                {p.label}
              </div>
            ))}
          </div>
          <input
            type="text"
            className="mc-perk-input"
            placeholder={t('manageClan.membershipCustomPlaceholder')}
            value={customPerk}
            onChange={(e) => setCustomPerk(e.target.value.slice(0, 80))}
            data-testid="mc-custom-perk"
          />
        </div>

        {/* Get Verified — Phase 7: wired to /verify */}
        </>)}
        {activeTab === 'grow' && (<>
        <div
          className="mc-section-card mc-cta-dark"
          style={{ background: 'linear-gradient(135deg, #1A2440, #2D3A66)', border: 'none', cursor: 'pointer' }}
          onClick={() => navigate('/verify')}
          role="button"
          tabIndex={0}
          data-testid="mc-verified-stub"
        >
          <div className="mc-section-head">
            <span className="mc-section-title" style={{ color: '#fff' }}>{t('manageClan.getVerifiedTitle')}</span>
            {user?.verified ? (
              <span className="mc-lock-badge" style={{ background: 'rgba(180, 255, 0,0.25)', color: '#7ed99a' }}>{t('manageClan.verifiedBadge')}</span>
            ) : (
              <span className="mc-lock-badge" style={{ background: 'rgba(255, 87, 34,0.2)', color: '#FFC107' }}>{t('manageClan.notApplied')}</span>
            )}
          </div>
          <p className="mc-section-sub" style={{ color: 'rgba(255,255,255,0.75)' }}>
            {user?.verified
              ? t('manageClan.verifiedBody')
              : t('manageClan.verifyBody')}
          </p>
          <div className="mc-locked-row" style={{ background: 'rgba(255,255,255,0.1)' }}>
            <span className="mc-locked-label" style={{ color: 'rgba(255,255,255,0.85)' }}>{t('manageClan.applicationStatus')}</span>
            <span className="mc-locked-value" style={{ color: '#fff' }}>
              {user?.verified ? t('manageClan.reviewBadge') : t('manageClan.applyNow')}
            </span>
          </div>
        </div>

        {/* Get Featured */}
        <div
          className="mc-section-card mc-cta-dark"
          style={{ background: 'linear-gradient(135deg, #2A2140, #3D2E5C)', border: 'none', cursor: 'pointer' }}
          onClick={() => navigate('/featured/buy')}
          data-testid="mc-featured-stub"
        >
          <div className="mc-section-head">
            <span className="mc-section-title" style={{ color: '#fff' }}>{t('manageClan.getFeaturedTitle')}</span>
            <span className="mc-lock-badge" style={{ background: 'rgba(255, 87, 34,0.2)', color: '#FFC107' }}>{t('manageClan.getFeaturedFromPrice')}</span>
          </div>
          <p className="mc-section-sub" style={{ color: 'rgba(255,255,255,0.75)' }}>
            {t('manageClan.getFeaturedLede')}
          </p>
          <div className="mc-locked-row" style={{ background: 'rgba(255,255,255,0.1)' }}>
            <span className="mc-locked-label" style={{ color: 'rgba(255,255,255,0.85)' }}>{t('manageClan.boostVisibility')}</span>
            <span className="mc-locked-value" style={{ color: '#fff' }}>{t('manageClan.buyASlot')}</span>
          </div>
        </div>

        {/* Pause new memberships */}
        </>)}
        {activeTab === 'danger' && (<>
        <div className="mc-section-card mc-danger">
          <div className="mc-section-head">
            <span className="mc-section-title">{t('manageClan.pauseTitle')}</span>
          </div>
          <div className="mc-toggle-row">
            <div className="mc-toggle-copy">
              <div className="mc-toggle-title">{t('manageClan.pauseCopyTitle')}</div>
              <div className="mc-toggle-sub">
                {t('manageClan.pauseCopySub')}
              </div>
            </div>
            <label className="mc-switch">
              <input
                type="checkbox"
                checked={paused}
                onChange={(e) => setPaused(e.target.checked)}
                data-testid="mc-pause-toggle"
              />
              <span className="mc-slider" />
            </label>
          </div>
        </div>
        </>)}

        {error && (
          <div className="mc-err-banner" data-testid="mc-save-err">{error}</div>
        )}

        {/* Sticky save bar */}
        <div
          className={`mc-save-bar${isDirty ? ' show' : ''}`}
          data-testid="mc-save-bar"
        >
          <span className="mc-save-msg" data-testid="mc-save-msg">{t('manageClan.unsavedChanges')}</span>
          <button className="mc-btn-discard" onClick={discardChanges} data-testid="mc-discard-btn">
            {t('manageClan.discard')}
          </button>
          <button
            className="mc-btn-save"
            onClick={save}
            disabled={saving}
            data-testid="mc-save-btn"
          >
            {saving ? t('manageClan.saving') : t('manageClan.saveChanges')}
          </button>
        </div>
      </div>

      {toast && <Toast msg={toast.msg} onDone={() => setToast(null)} />}
      {featureOpen && (
        <FeatureClanSheet
          onClose={() => setFeatureOpen(false)}
          onPurchased={(slot) => {
            setFeatureOpen(false);
            setToast({ msg: `⭐ Featured until ${new Date(slot.expires_at).toLocaleDateString()}` });
          }}
        />
      )}
      <ShareClanModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        handle={clan?.handle}
      />
      <ReplayModal
        open={replayModalOpen}
        handle={clan?.handle}
        onClose={() => setReplayModalOpen(false)}
      />
    </>
  );
}
